import React, { useState, useEffect } from 'react';
import { AlertTriangle, Loader2, LogOut, ShoppingBag, Settings } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { useNavigate } from 'react-router-dom';

/**
 * SalesAuthGuard - Protects sales routes with server-side authentication
 * 
 * Authentication Flow:
 * - POST /api/sales/login validates password server-side using SALES_PASSWORD
 * - Sets HttpOnly, Secure, SameSite=Strict session cookie with 24-hour TTL
 * - GET /api/sales/session verifies HMAC-signed tokens using SALES_SESSION_SECRET
 * - POST /api/sales/logout clears session cookie with Max-Age=0
 * 
 * Security Features:
 * - HMAC-SHA256 signed session tokens prevent forgery
 * - CORS origin allowlist (ALLOWED_ORIGINS) prevents credential hijacking
 * - No client-side password storage or validation
 * - Independent from admin authentication system
 */
const SalesAuthGuard = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isAdminAuthenticated } = useAdminAuth();
  const navigate = useNavigate();

  const SALES_AUTH_KEY = 'sales_authenticated';
  const SALES_SESSION_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  // Helper function to manage sales session
  const getSalesSession = () => {
    try {
      const sessionData = localStorage.getItem(SALES_AUTH_KEY);
      if (!sessionData) return null;
      
      const session = JSON.parse(sessionData);
      if (!session || !session.authenticated || !session.expiresAt) {
        return null;
      }
      
      // Check if session is expired
      if (Date.now() > session.expiresAt) {
        localStorage.removeItem(SALES_AUTH_KEY);
        return null;
      }
      
      return session;
    } catch (error) {
      console.warn('Error reading sales session:', error);
      localStorage.removeItem(SALES_AUTH_KEY);
      return null;
    }
  };

  const setSalesSession = (authenticated = true, serverProvidedExpiresAt = null) => {
    try {
      let expiresAt;
      if (serverProvidedExpiresAt) {
        // Convert server timestamp to epoch milliseconds if needed
        expiresAt = typeof serverProvidedExpiresAt === 'string' 
          ? new Date(serverProvidedExpiresAt).getTime()
          : typeof serverProvidedExpiresAt === 'number' && serverProvidedExpiresAt < 2000000000
          ? serverProvidedExpiresAt * 1000 // Convert Unix seconds to milliseconds
          : serverProvidedExpiresAt;
      } else {
        // Fallback to client-side TTL calculation
        expiresAt = Date.now() + SALES_SESSION_TTL;
      }
      
      const session = {
        authenticated,
        expiresAt,
        createdAt: Date.now()
      };
      localStorage.setItem(SALES_AUTH_KEY, JSON.stringify(session));
    } catch (error) {
      console.error('Error saving sales session:', error);
    }
  };

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // DEVELOPMENT BYPASS: Skip authentication if env var is set
        if (import.meta.env.VITE_BYPASS_SALES_AUTH_DEV === 'true') {
          // Development mode: Bypassing sales authentication (VITE_BYPASS_SALES_AUTH_DEV=true)
          setIsAuthenticated(true);
          setLoading(false);
          return;
        }

        // First check local session (for performance)
        const salesSession = getSalesSession();
        let isSalesAuth = salesSession && salesSession.authenticated;

        // If we have a local session, also verify with server
        if (isSalesAuth) {
          try {
            const response = await fetch('/api/sales/session', {
              method: 'GET',
              credentials: 'include',
            });
            const result = await response.json();
            isSalesAuth = result.isAuthenticated;
            
            // If server says invalid, clear local session
            if (!isSalesAuth) {
              localStorage.removeItem(SALES_AUTH_KEY);
            }
          } catch (serverError) {
            console.warn('Server session check failed, using local session:', serverError);
            // Keep local session if server check fails (offline scenario)
          }
        }

        // Admin users have automatic access to sales dashboard
        setIsAuthenticated(isSalesAuth || isAdminAuthenticated);
      } catch (error) {
        console.warn('Error checking sales auth:', error);
        setIsAuthenticated(isAdminAuthenticated);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [isAdminAuthenticated]);

  // Listen for storage changes to sync across tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === SALES_AUTH_KEY) {
        const salesSession = getSalesSession();
        const isSalesAuth = salesSession && salesSession.authenticated;
        // Preserve admin auth state when syncing
        setIsAuthenticated(isSalesAuth || isAdminAuthenticated);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [isAdminAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Call server-side authentication endpoint
      const response = await fetch('/api/sales/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies
        body: JSON.stringify({ password }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Use server-provided expiry if available
        setSalesSession(true, result.expiresAt);
        setIsAuthenticated(true);
        // Clear password from memory after successful authentication
        setPassword('');
      } else {
        setError(result.error || 'Invalid password. Please try again.');
      }
    } catch (error) {
      console.error('Sales login error:', error);
      setError('Login failed. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Call server-side logout endpoint to clear HttpOnly cookie
      const response = await fetch('/api/sales/logout', {
        method: 'POST',
        credentials: 'include', // Include cookies for logout
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Logout request failed');
      }

      // Only clear local state after successful server logout
      localStorage.removeItem(SALES_AUTH_KEY);
      // Only update state if we're not admin authenticated (preserve admin access)
      setIsAuthenticated(isAdminAuthenticated);
      setPassword('');
      setError('');
    } catch (error) {
      console.error('Sales logout error:', error);
      // Even if server logout fails, clear local state for security
      // (HttpOnly cookie might already be expired)
      try {
        localStorage.removeItem(SALES_AUTH_KEY);
        setIsAuthenticated(isAdminAuthenticated);
        setPassword('');
        setError('');
      } catch (localError) {
        console.error('Failed to clear local auth state:', localError);
      }
    }
  };

  // Show loading spinner during initial auth check
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" aria-live="polite">
        <div className="text-center" role="status" aria-label="Checking authentication">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-base text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
              <ShoppingBag className="h-6 w-6 text-blue-600" />
            </div>
            <CardTitle className="text-xl font-semibold">Sales Team Access</CardTitle>
            <CardDescription>
              Please enter the sales team password to access the dashboard
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Input
                  type="password"
                  placeholder="Enter sales password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isSubmitting}
                  className="w-full"
                  autoFocus
                />
              </div>
              
              {error && (
                <div className="flex items-center space-x-2 text-sm text-red-600 bg-red-50 p-3 rounded-md">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              
              <Button 
                type="submit" 
                className="w-full" 
                disabled={!password.trim() || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
            
            <div className="mt-6 pt-4 border-t border-gray-200 text-center">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/')}
                className="text-sm"
              >
                Back to Main App
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // User is authenticated - render sales interface with header
  return (
    <div className="sales-interface">
      {/* Sales header with logout */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
              {isAdminAuthenticated ? (
                <Settings className="h-4 w-4 text-blue-600" />
              ) : (
                <ShoppingBag className="h-4 w-4 text-blue-600" />
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                {isAdminAuthenticated ? 'Admin Access' : 'Sales Team'}
              </p>
              <p className="text-xs text-gray-500">
                {isAdminAuthenticated ? 'Full Dashboard Access' : 'Dashboard Access'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {isAdminAuthenticated && (
              <Button
                variant="outline"
                size="sm"
                asChild
                className="text-sm"
              >
                <a href="/admin">
                  <Settings className="h-4 w-4 mr-2" />
                  Admin Panel
                </a>
              </Button>
            )}
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="text-sm"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </div>
      
      {/* Sales content */}
      <div className="sales-content">
        {children}
      </div>
    </div>
  );
};

export default SalesAuthGuard;